import { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams, useResolvedPath } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { InventoryCountWithProducts } from "@/interfaces";
import { baseUrl } from "@/consts";
import { InventoryCountHelper } from "@/helpers";
import { useDataApi } from "@/hooks";
import { formatDateToPST, isNonEmptyArray } from "@/utils";
import {
  colors,
  sxButtonBase,
  sxFont_18_400,
  sxFont_24_400,
  sxFont_32_500,
} from "@/theme";
import {
  CrudGridTest,
  InventoryCountWithProductsEditDialog,
} from "@/components/organisms";
import { CrudGridRef } from "@/components/organisms/CrudGrid/interface";
import { West } from "@mui/icons-material";

const columns: GridColDef[] = [
  { field: "productDescription", headerName: "PRODUCT", width: 300, maxWidth: 300 },
  { field: "sku", headerName: "SKU", width: 200 },
  {
    field: "counted",
    headerName: "COUNTED",
    type: "number",
    flex: 1,
    valueGetter: (_, row) => InventoryCountHelper.getProductCounted(row),
  },
  {
    field: "inventoryCount",
    headerName: "EXPECTED",
    type: "number",
    sortable: false,
    disableColumnMenu: true,
    flex: 1,
  },
  {
    field: "discrepancy",
    headerName: "DISCREPANCY",
    type: "number",
    flex: 1,
    valueGetter: (_, row) => InventoryCountHelper.getProductDiscrepancy(row),
  },
];

const InventoryCountEditForm: React.FC = () => {
  const { inventoryCountId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const parentPath = useResolvedPath("../inventoryCounts").pathname;
  const childRef = useRef<CrudGridRef | null>(null);

  const [inventoryCountWithProductsToEdit, setInventoryCountWithProductsToEdit] =
    useState<InventoryCountWithProducts | undefined>(undefined);
  const [isInventoryCountWithProductsEditDialogOpen, setIsInventoryCountWithProductsEditDialogOpen] =
    useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [tabIndex, setTabIndex] = useState(0);

  const { put } = useDataApi(baseUrl.INVENTORY_COUNTS);
  const { data: historyData, setUrl: setHistoryUrl, isLoading: isHistoryLoading } = useDataApi();

  // Calculate sums
  const countedSum = tableRows.reduce((sum, row) => {
    const rowCounted = Array.isArray(row.inventoryCountWithInventoryCountProducts)
      ? row.inventoryCountWithInventoryCountProducts.reduce(
          (subSum: number, prod: any) => subSum + (prod.counted ?? 0),
          0
        )
      : (row.counted ?? 0);
    return sum + rowCounted;
  }, 0);

  const expectedSum = tableRows.reduce((sum, row) => sum + (row.inventoryCount ?? 0), 0);

  const discrepancySum = tableRows.reduce((sum, row) => {
    const rowCounted = Array.isArray(row.inventoryCountWithInventoryCountProducts)
      ? row.inventoryCountWithInventoryCountProducts.reduce(
          (subSum: number, prod: any) => subSum + (prod.counted ?? 0),
          0
        )
      : (row.counted ?? 0);
    const expected = row.inventoryCount ?? 0;
    return sum + (rowCounted - expected);
  }, 0);

  // Fetch history data when Count Logs tab is selected
  useEffect(() => {
    if (tabIndex === 1 && inventoryCountId) {
      setHistoryUrl(
        `${baseUrl.INVENTORY_COUNTS}/history?inventoryCountId=${inventoryCountId}&top=1000`
      );
    }
  }, [tabIndex, inventoryCountId, setHistoryUrl]);

  const handleBackToInventoryCountsClick = () => {
    navigate(parentPath);
  };

  const reloadData = () => {
    childRef?.current?.reloadData?.();
  };

  const editInventoryCountProducts = (
    inventoryCountWithProducts?: InventoryCountWithProducts
  ) => {
    setInventoryCountWithProductsToEdit(inventoryCountWithProducts);
    setIsInventoryCountWithProductsEditDialogOpen(true);
  };

  const onInventoryCountWithProductsEditDialogClose = (
    inventoryCountWithProducts?: InventoryCountWithProducts
  ) => {
    setIsInventoryCountWithProductsEditDialogOpen(false);
    setInventoryCountWithProductsToEdit(undefined);

    if (inventoryCountWithProducts) {
      reloadData();
    }
  };

  const handleCloseCount = async () => {
    try {
      const currentDate = new Date().toISOString();
      const inventoryCount = location.state?.inventoryCount;

      const payload = {
        inventoryCountId: Number(inventoryCountId),
        name: inventoryCount?.name || "Test Count",
        dateStarted: inventoryCount?.dateStarted || new Date().toISOString(),
        dateClosed: currentDate,
        createdBy: inventoryCount?.createdBy || "",
        createdOn: inventoryCount?.createdOn || new Date().toISOString(),
        numberOfProducts: tableRows.length,
        counted: countedSum,
        dealerFirstName: inventoryCount?.dealerFirstName || "",
        dealerLastName: inventoryCount?.dealerLastName || "",
        suppliers:
          location.state?.suppliers?.map((supplier: any) => ({
            supplierId: supplier.supplierId,
            supplierName: supplier.supplierName,
          })) || [],
      };

      await put(`${baseUrl.INVENTORY_COUNTS}/${inventoryCountId}`, payload);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error closing count:", error);
    }
  };

  const handleContinuePress = () => {
    navigate(parentPath);
  };

  const refreshHistoryData = () => {
    setHistoryUrl(
      `${baseUrl.INVENTORY_COUNTS}/history?inventoryCountId=${inventoryCountId}&top=1000&t=${Date.now()}`
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "70px 1fr 1fr",
          alignItems: "center",
          height: "120px",
          padding: "0 28px 0 40px",
        }}
      >
        <Button
          startIcon={<West />}
          onClick={handleBackToInventoryCountsClick}
          sx={{
            justifySelf: "start",
            ...sxFont_24_400,
            color: colors.neutralGrey800,
            ...sxButtonBase,
          }}
        ></Button>
        <Box>
          <Typography
            component="div"
            sx={{ ...sxFont_32_500, color: colors.neutralGrey800 }}
          >
            Opening Inventory Count
          </Typography>
          <Typography
            component="div"
            sx={{ ...sxFont_18_400, color: colors.neutralGrey700 }}
          >
            Suppliers:{" "}
            {location.state?.suppliers
              ?.map((supplier: any) => supplier.supplierName)
              .join(", ")}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <Typography
              component="span"
              sx={{ fontWeight: 700, color: colors.neutralGrey800 }}
            >
              Counted:
            </Typography>
            <Typography
              component="span"
              sx={{ color: colors.neutralGrey800, mr: 2 }}
            >
              {countedSum.toLocaleString()}
            </Typography>
            <Typography
              component="span"
              sx={{ fontWeight: 700, color: colors.neutralGrey800 }}
            >
              Expected:
            </Typography>
            <Typography
              component="span"
              sx={{ color: colors.neutralGrey800, mr: 2 }}
            >
              {expectedSum.toLocaleString()}
            </Typography>
            <Typography
              component="span"
              sx={{ fontWeight: 700, color: colors.neutralGrey800 }}
            >
              Discrepancy:
            </Typography>
            <Typography
              component="span"
              sx={{
                color: discrepancySum >= 0 ? colors.mutedTealGreen : colors.red,
                fontWeight: 700,
                mr: 3,
              }}
            >
              {discrepancySum.toLocaleString()}
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleCloseCount}
            sx={{
              background: colors.primaryOzone500,
              color: "#fff",
              borderRadius: 2,
              fontFamily: "PF DIN Text Pro",
              fontWeight: 500,
              px: 3,
              py: 1,
              minWidth: 155,
            }}
          >
            Close Count
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          padding: "0 28px 0 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Products" />
          <Tab label="Count Logs" />
        </Tabs>
        {tabIndex === 1 && (
          <Button onClick={refreshHistoryData} sx={{ marginLeft: 2 }}>
            Refresh
          </Button>
        )}
      </Box>

      {tabIndex === 0 && (
        <CrudGridTest
          ref={childRef}
          baseUrl={baseUrl.INVENTORY_COUNTS}
          path="getproducts"
          additionalURLSearchParams={{
            inventoryCountId: inventoryCountId as string,
          }}
          columns={columns}
          getRowId={(row) => row.productId}
          setRowId={(row, id) => {
            row.productId = id;
          }}
          onEditRow={editInventoryCountProducts}
          onRowsChange={setTableRows}
        />
      )}

      {tabIndex === 1 && (
        <Box sx={{ padding: "0 28px 0 40px", height: 600 }}>
          <DataGrid
            rows={isNonEmptyArray(historyData) ? historyData : []}
            columns={
              [
                { field: "historyId", headerName: "ID", width: 80 },
                { field: "productId", headerName: "PRODUCT ID", width: 120 },
                { field: "oldCounted", headerName: "OLD", type: "number", width: 100 },
                { field: "newCounted", headerName: "NEW", type: "number", width: 100 },
                { field: "action", headerName: "ACTION", width: 120 },
                { field: "changedBy", headerName: "CHANGED BY", flex: 1 },
                {
                  field: "changedOn",
                  headerName: "CHANGED ON",
                  width: 220,
                  renderCell: (params: any) => {
                    const raw = params?.value ?? params?.row?.changedOn;
                    const formatted = formatDateToPST(raw);
                    if (formatted) return formatted;

                    try {
                      const d = raw ? new Date(raw) : undefined;
                      if (d && !isNaN(d.getTime())) return d.toLocaleString();
                    } catch (e) {
                      /* fallthrough */
                    }

                    return raw ?? "";
                  },
                },
              ] as GridColDef[]
            }
            getRowId={(row) => row.historyId}
            loading={isHistoryLoading}
            autoHeight={false}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      )}

      {inventoryCountWithProductsToEdit ? (
        <InventoryCountWithProductsEditDialog
          inventoryCountWithProducts={inventoryCountWithProductsToEdit}
          open={isInventoryCountWithProductsEditDialogOpen}
          onClose={onInventoryCountWithProductsEditDialogClose}
        />
      ) : undefined}

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        sx={{
          "& .MuiDialog-paper": {
            px: 4,
            pt: 3,
            borderRadius: 3,
          },
        }}
      >
        <DialogContent>
          <DialogContentText>
            <Typography
              sx={{
                color: colors.neutralGrey800,
                fontFamily: "PF DIN Text Pro",
                fontSize: "20px",
              }}
            >
              Count successfully closed. <br />
              You have no discrepancies.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ m: "auto", mb: 2 }}>
          <Button
            onClick={() => handleContinuePress()}
            variant="outlined"
            sx={{
              borderColor: colors.neutralGrey800,
              fontFamily: "PF DIN Text Pro",
              bgcolor: "transparent",
              color: colors.neutralGrey800,
              borderRadius: "8px",
              px: 2,
              ":hover": {
                backgroundColor: colors.neutralGrey200,
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryCountEditForm;
